import React, { FC, useCallback, useMemo, useReducer } from 'react';
import { DataMutationContext } from './const';
import { DataMutationContextProps, DataMutationItem, ReducerStateProps } from './types/dataMutationProvider';
import { QueryKey } from './types';

const DEFAULT_STATE: ReducerStateProps = {
	mutations: [],
};

type ReducerActions =
	| {
			type: 'ADD_MUTATION';
			payload: DataMutationItem;
	  }
	| {
			type: 'REMOVE_MUTATION';
			payload: QueryKey;
	  };

function reducer(state: ReducerStateProps = DEFAULT_STATE, action: ReducerActions): ReducerStateProps {
	switch (action.type) {
		case 'ADD_MUTATION': {
			const newMutations = state.mutations.filter((i) => i.key !== action.payload.key);

			return {
				...state,
				mutations: [...newMutations, action.payload],
			};
		}

		case 'REMOVE_MUTATION': {
			const newMutations = state.mutations.filter((i) => i.key !== action.payload);

			return {
				...state,
				mutations: newMutations,
			};
		}

		default: {
			return state;
		}
	}
}

export const DataMutationProvider: FC<React.PropsWithChildren> = ({ children }) => {
	const [reducerState, dispatch] = useReducer(reducer, DEFAULT_STATE);
	const addMutation = useCallback((mutation: DataMutationItem) => {
		dispatch({
			type: 'ADD_MUTATION',
			payload: mutation,
		});
	}, []);
	const removeMutation = useCallback((mutationKey: QueryKey) => {
		dispatch({
			type: 'REMOVE_MUTATION',
			payload: mutationKey,
		});
	}, []);
	const providerValue = useMemo<DataMutationContextProps>(() => {
		return {
			addMutation,
			removeMutation,
			mutations: reducerState.mutations,
		};
	}, [addMutation, reducerState.mutations, removeMutation]);

	return <DataMutationContext.Provider value={providerValue}>{children}</DataMutationContext.Provider>;
};
